import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from "react";
import { scroller } from "react-scroll";
import { Order, OrderItems } from "~/application/types";

export interface BrokenPolicyJustification {
  uuid: string;
  justification?: string;
  policyViolationJustificationId?: string;
  attachment?: File;
}

export interface IBrokenPolicyJustificationContext {
  brokenPolicyItems: BrokenPolicyJustification[];
  allBrokenPolicyItemsAreJustificated: boolean;
  setBrokenPolicyItems: Dispatch<SetStateAction<BrokenPolicyJustification[]>>;
  isSubmitAttempted: boolean;
  setIsSubmitAttempted: Dispatch<SetStateAction<boolean>>;
  scrollToSection: (items: string[]) => void;
}

interface BrokenPolicyJustificationProviderParams {
  order?: Order;
  children: ReactNode;
}

export const BrokenPolicyJustificationContext = createContext<IBrokenPolicyJustificationContext>(
  {} as IBrokenPolicyJustificationContext,
);

export const useBrokenPolicyJustification = () => useContext(BrokenPolicyJustificationContext);

export function BrokenPolicyJustificationProvider({
  order,
  children,
}: BrokenPolicyJustificationProviderParams) {
  const [brokenPolicyItems, setBrokenPolicyItems] = useState<BrokenPolicyJustification[]>([]);

  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);

  const scrollToSection = (items: string[]) => {
    const result = items.filter((item) => !brokenPolicyItems.some((bp) => bp.uuid === item));

    if (result.length > 0) {
      const navBarHeight =
        document.querySelector("[data-nav-bar]")?.getBoundingClientRect().height || 0;
      const bookingBarHeight =
        document.querySelector("[data-booking-bar]")?.getBoundingClientRect().height || 0;

      scroller.scrollTo(`scroll-${result[0]}`, {
        offset: -(navBarHeight + bookingBarHeight + 16),
      });
    }
  };

  const airwayBrokenPolicyAmount =
    order?.items[OrderItems.AIRWAY]?.flights.reduce(
      (acc, flight) => (flight.violatedPolicies?.length ? acc + 1 : acc),
      0,
    ) || 0;

  const roadBrokenPolicyAmount =
    order?.items[OrderItems.ROAD]?.travels.reduce(
      (acc, travel) => (travel.violatedPolicies?.length ? acc + 1 : acc),
      0,
    ) || 0;

  const hotelBrokenPolicyAmount =
    order?.items[OrderItems.HOTEL]?.rooms.reduce(
      (acc, room) => (room.violatedPolicies?.length ? acc + 1 : acc),
      0,
    ) || 0;

  const vehicleBrokenPolicyAmount =
    order?.items[OrderItems.VEHICLE]?.vehicles.reduce(
      (acc, vehicle) => (vehicle.violatedPolicies?.length ? acc + 1 : acc),
      0,
    ) || 0;

  const brokenPolicyItemsAmount =
    airwayBrokenPolicyAmount +
    roadBrokenPolicyAmount +
    hotelBrokenPolicyAmount +
    vehicleBrokenPolicyAmount;

  const allBrokenPolicyItemsAreJustificated = brokenPolicyItemsAmount === brokenPolicyItems.length;

  return (
    <BrokenPolicyJustificationContext.Provider
      value={{
        brokenPolicyItems,
        allBrokenPolicyItemsAreJustificated,
        setBrokenPolicyItems,
        scrollToSection,
        isSubmitAttempted,
        setIsSubmitAttempted,
      }}
    >
      {children}
    </BrokenPolicyJustificationContext.Provider>
  );
}
